import type { ViewerShell } from '../../shell';
import type { AssistantApi } from './api';
import { aiLimitText } from './errors';
import { IndexedDbClipBackend, type KeptClip, KeptClipStore, type KeptReason } from './kept-clips';
import { KeptClipsView } from './kept-clips-view';
import { type RetryOptions, withRetry } from './transcribe-retry';

/**
 * Dictation for the Asistente: the microphone is recorded in the browser (MediaRecorder) and the clip
 * goes to ris for transcription; the text lands in the input for the user to read and send.
 * A dictation is never thrown away: a failed transcription (after its retries), a clip too large to
 * send, a recording cut by the time limit or by hiding the tab, all end with the audio either
 * transcribed or kept (memory + IndexedDB, see kept-clips.ts) with Reintentar / Descargar audio.
 * Hidden where it cannot work: no MediaRecorder, no getUserMedia, or a page that is not a secure context.
 */

/** A recording stops by itself after this long (one timer, not a loop) and is transcribed as is. */
export const MAX_RECORDING_MS = 60_000;
/** ris accepts clips up to 10 MB (contract §2 /transcribe). */
export const MAX_AUDIO_BYTES = 10 * 1024 * 1024;
/** The recorder hands over audio every second, so a page closed mid-recording still has most of it. */
const TIMESLICE_MS = 1_000;
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

export function voiceSupported(): boolean {
  return window.isSecureContext
    && typeof window.MediaRecorder === 'function'
    && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

function pickMime(): string | undefined {
  const isSupported = window.MediaRecorder.isTypeSupported?.bind(window.MediaRecorder);
  return isSupported ? MIME_CANDIDATES.find((m) => isSupported(m)) : undefined;
}

/**
 * One recording at a time; `stop()` resolves with the clip (empty Blob when nothing was captured).
 * At `maxMs` the recording is finalized by itself and handed to `onAutoStop`, never dropped.
 */
export class VoiceRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private mime = 'audio/webm';
  private timer: ReturnType<typeof setTimeout> | null = null;
  private done: ((clip: Blob) => void) | null = null;
  private starting = false;
  /** Called when the recording stopped by itself (time limit), with the clip. */
  onAutoStop: ((clip: Blob) => void) | null = null;

  constructor(private readonly maxMs = MAX_RECORDING_MS) {}

  get recording(): boolean {
    return this.recorder?.state === 'recording';
  }

  async start(): Promise<void> {
    // A second click while the browser still asks for the microphone must not open a second stream.
    if (this.recording || this.starting) return;
    this.starting = true;
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } finally {
      this.starting = false;
    }
    const mime = pickMime();
    this.chunks = [];
    try {
      const recorder = new MediaRecorder(this.stream, mime ? { mimeType: mime } : undefined);
      recorder.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) this.chunks.push(event.data);
      });
      recorder.addEventListener('stop', () => this.finish());
      this.mime = (recorder.mimeType || mime || 'audio/webm').split(';')[0];
      recorder.start(TIMESLICE_MS);
      this.recorder = recorder;
    } catch (error) {
      // The microphone is already open: never leave it on when the recorder cannot run.
      this.release();
      throw error;
    }
    this.timer = setTimeout(() => {
      this.done = (clip) => this.onAutoStop?.(clip);
      this.recorder?.stop();
    }, this.maxMs);
  }

  stop(): Promise<Blob> {
    return new Promise((resolve) => {
      if (!this.recorder || this.recorder.state === 'inactive') {
        resolve(new Blob());
        return;
      }
      this.done = resolve;
      this.recorder.stop();
    });
  }

  /** What was captured so far, without stopping (the page is closing: keep what there is). */
  partial(): Blob {
    return new Blob(this.chunks, { type: this.mime });
  }

  /** Releases the microphone without keeping anything. Only for a recording nobody dictated into. */
  cancel(): void {
    this.done = null;
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    else this.release();
  }

  private finish(): void {
    const clip = new Blob(this.chunks, { type: this.mime });
    this.chunks = [];
    this.release();
    const done = this.done;
    this.done = null;
    done?.(clip);
  }

  private release(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
  }
}

export const VOICE_TEXT = {
  dictate: 'Dictar',
  stop: 'Detener',
  dictateLabel: 'Dictar la pregunta',
  stopLabel: 'Detener el dictado',
  transcribing: 'Transcribiendo…',
  micError: 'No fue posible acceder al micrófono.',
  empty: 'No se grabó audio.',
  capped: 'Se alcanzó el límite de 60 segundos; se transcribe lo grabado.',
  noText: 'No se reconoció texto en el audio; quedó guardado para reintentar o descargar.',
  tooLong: 'La grabación es demasiado grande para transcribirla; quedó guardada para descargar.',
  failed: 'No fue posible transcribir el audio.',
  kept: 'El audio quedó guardado para reintentar o descargar.',
} as const;

/** The transcription seam, so the unit spec can run the button's logic without ris. */
export type Transcribe = (study: string, clip: Blob, signal: AbortSignal) => Promise<string>;

/** Seams for the unit spec; the panel passes none. */
export interface VoiceButtonOptions {
  store?: KeptClipStore;
  recorder?: VoiceRecorder;
  supported?: boolean;
  retry?: RetryOptions;
}

/** The "Dictar" button: record → transcribe in ris → text into the input. Null element when unsupported. */
export class VoiceButton {
  readonly element: HTMLButtonElement | null;
  /** "Dictados sin transcribir" (null when dictation is unsupported). */
  readonly keptElement: HTMLElement | null;
  readonly store: KeptClipStore;
  private readonly recorder: VoiceRecorder;
  private readonly view: KeptClipsView | null;
  private readonly transcribeCall: Transcribe;
  private readonly retryOptions: RetryOptions;
  private transcribing = false;
  /** The study the running recording belongs to (the user may switch while speaking). */
  private recordingStudy = '';

  constructor(
    private readonly shell: Pick<ViewerShell, 'toast'>,
    api: Pick<AssistantApi, 'transcribe'>,
    private readonly onText: (text: string) => void,
    private readonly study: () => string,
    options: VoiceButtonOptions = {},
  ) {
    this.transcribeCall = (study, clip, signal) => api.transcribe(study, clip, signal);
    this.retryOptions = options.retry ?? {};
    this.store = options.store ?? new KeptClipStore(new IndexedDbClipBackend());
    this.recorder = options.recorder ?? new VoiceRecorder();
    // The time limit finalizes the clip: transcribed, or kept when that fails.
    this.recorder.onAutoStop = (clip) => {
      this.shell.toast(VOICE_TEXT.capped, 'info');
      void this.transcribe(clip, this.recordingStudy);
    };
    if (!(options.supported ?? voiceSupported())) {
      this.element = null;
      this.keptElement = null;
      this.view = null;
      return;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'assistant-btn assistant-mic';
    button.addEventListener('click', () => void this.toggle());
    this.element = button;
    this.view = new KeptClipsView(this.store, (clip) => this.retry(clip));
    this.keptElement = this.view.element;
    // Closing or reloading the page mid-recording: keep what was captured (best effort, async write).
    window.addEventListener('pagehide', () => this.keepPartial());
    this.render();
  }

  get recording(): boolean {
    return this.recorder.recording;
  }

  private render(label?: string): void {
    const button = this.element;
    if (!button) return;
    const recording = this.recorder.recording;
    button.textContent = label ?? (recording ? VOICE_TEXT.stop : VOICE_TEXT.dictate);
    button.setAttribute('aria-label', recording ? VOICE_TEXT.stopLabel : VOICE_TEXT.dictateLabel);
    button.setAttribute('aria-pressed', String(recording));
    button.classList.toggle('recording', recording);
    button.disabled = this.transcribing;
    this.view?.setBusy(this.transcribing || recording);
  }

  private async toggle(): Promise<void> {
    if (this.transcribing) return;
    if (this.recorder.recording) {
      await this.finalize();
      return;
    }
    this.recordingStudy = this.study();
    try {
      await this.recorder.start();
    } catch (error) {
      console.warn('[viewer] microphone unavailable', error);
      this.shell.toast(VOICE_TEXT.micError, 'error');
    }
    this.render();
  }

  /** Stops a recording in progress and transcribes it (kept on failure). Used on Detener and on hide. */
  async finalize(): Promise<void> {
    if (!this.recorder.recording) return;
    const study = this.recordingStudy;
    await this.transcribe(await this.recorder.stop(), study);
  }

  /** Transcribes a clip; anything but text in the input ends with the clip kept. */
  async transcribe(clip: Blob, study: string): Promise<void> {
    if (clip.size === 0) {
      this.shell.toast(VOICE_TEXT.empty, 'warning');
      this.render();
      return;
    }
    if (clip.size > MAX_AUDIO_BYTES) {
      await this.keep(study, clip, 'too_large');
      this.shell.toast(VOICE_TEXT.tooLong, 'warning');
      this.render();
      return;
    }
    this.transcribing = true;
    this.render(VOICE_TEXT.transcribing);
    try {
      const text = await this.send(study, clip);
      if (text === null) await this.keep(study, clip, 'failed');
      else if (!text.trim()) {
        await this.keep(study, clip, 'failed');
        this.shell.toast(VOICE_TEXT.noText, 'warning');
      } else this.onText(text);
    } finally {
      this.transcribing = false;
      this.render();
    }
  }

  /** "Reintentar" on a kept clip: on text it goes into the input and leaves the list. */
  private async retry(clip: KeptClip): Promise<void> {
    if (this.transcribing || this.recorder.recording) return;
    this.transcribing = true;
    this.render(VOICE_TEXT.transcribing);
    try {
      const text = await this.send(clip.study, clip.audio);
      if (text === null) return;
      if (!text.trim()) {
        this.shell.toast(VOICE_TEXT.noText, 'warning');
        return;
      }
      this.onText(text);
      await this.store.remove(clip.id);
    } finally {
      this.transcribing = false;
      this.render();
    }
  }

  /** The transcript, or null after a failure that was already toasted. */
  private async send(study: string, clip: Blob): Promise<string | null> {
    try {
      return await withRetry((signal) => this.transcribeCall(study, clip, signal), this.retryOptions);
    } catch (error) {
      console.warn('[viewer] transcription failed', error);
      this.shell.toast(`${aiLimitText(error) ?? VOICE_TEXT.failed} ${VOICE_TEXT.kept}`, 'error');
      return null;
    }
  }

  private async keep(study: string, clip: Blob, reason: KeptReason): Promise<void> {
    await this.store.keep(study, clip, reason);
  }

  private keepPartial(): void {
    if (!this.recorder.recording) return;
    const clip = this.recorder.partial();
    if (clip.size > 0) void this.keep(this.recordingStudy, clip, 'interrupted');
  }
}
